import {arktypeResolver} from '@hookform/resolvers/arktype'
import {FieldError, Form, Input, Label, TextField} from 'react-aria-components'
import {Controller, useForm} from 'react-hook-form'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import {contactAtom, useSetContact} from '@/lib/payment/store'
import noThrow from '@/utils/schema/noThrow'

import style from './ContactInfo.module.scss'
import {Contact} from './model'

export interface ContactInfoControl {
  submit: () => Promise<Contact>
}

interface Props {
  ref?: Ref<ContactInfoControl>
  className?: string
  initialContact?: Contact
}

const NoThrowContact = noThrow(Contact)

export default memo<Props>(function ContactInfo({ref, className, initialContact}) {
  const store = useStore()
  const account = useAccountInformationValue()
  const isAuthenticated = useIsAuthenticated()
  const setContact = useSetContact()
  const defaultContact = store.get(contactAtom)

  const formRef = useRef<HTMLFormElement>(null)

  // ---------------------------------------------------------------------------

  const defaultValues = useMemo(
    () =>
      account
        ? {
            email: account.email,
          }
        : (initialContact ?? defaultContact),
    [account, defaultContact, initialContact],
  )

  const {
    handleSubmit: handleContactSubmit,
    control: contactControl,
    trigger,
    getValues,
    formState,
    watch,
  } = useForm({
    mode: 'onTouched',
    resolver: arktypeResolver(NoThrowContact),
    defaultValues,
  })

  const latestFormState = useLatest(formState)
  const latestGetValues = useLatest(getValues)
  const latestTrigger = useStableCallback(trigger)

  useEffect(
    function persistDefaultValue() {
      setContact(defaultValues)
    },
    [setContact, defaultValues],
  )

  useEffect(
    function persistData() {
      const subscription = watch(() => {
        setContact(latestGetValues.current())
      })

      return () => {
        subscription.unsubscribe()
      }
    },
    [watch, setContact],
  )

  useImperativeHandle(ref, () => {
    return {
      submit: async () => {
        const result = await latestTrigger(undefined, {shouldFocus: true})
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- contains multiple errors
        if (!result) throw latestFormState.current.errors
        return latestGetValues.current()
      },
    }
  }, [])

  return (
    <Form
      ref={formRef}
      onSubmit={handleContactSubmit(() => {
        // do nothing
      })}
      className={clsx(style.Wrapper, className)}
    >
      <Controller
        control={contactControl}
        name='email'
        render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            validationBehavior='aria'
            isInvalid={invalid}
            isRequired
            className={style.Field}
            isReadOnly={isAuthenticated}
          >
            <Label className={style.Label}>Email</Label>
            <Input className={style.Input} ref={ref} />
            {!!error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
          </TextField>
        )}
      />
    </Form>
  )
})
